import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiAlertCircle, FiTrendingDown, FiHelpCircle, FiClock, FiX, FiPhone } = FiIcons;

const UrgencySection = ({ onBooking }) => {
  const painPoints = [
    {
      icon: FiTrendingDown,
      title: "เงินไม่พอใช้ ไม่ว่าจะทำงานหนักแค่ไหน?",
      description: "รายได้มีแต่ไม่เคยเหลือ ทำงานหนักแต่เงินไม่เข้ากระเป๋า",
      consequence: "ถ้าไม่แก้ไข คุณจะติดวงจรนี้ตลอดไป"
    },
    {
      icon: FiAlertCircle,
      title: "รู้สึกว่าชะตาการเงินไม่ดี?",
      description: "มีโอกาสทำเงินแต่มักพลาดไป หรือมีปัญหาขัดขวางเสมอ",
      consequence: "โอกาสดีๆ จะหลุดมือไปเรื่อยๆ"
    },
    {
      icon: FiHelpCircle,
      title: "ไม่รู้ว่าจะเสริมดวงการเงินอย่างไร?",
      description: "ลองหลายวิธีแล้วแต่ไม่เห็นผล หรือไม่รู้ว่าจะเริ่มจากตรงไหน",
      consequence: "เสียเวลาและเงินไปกับวิธีที่ผิด"
    }
  ];

  const urgencyFactors = [
    {
      icon: FiClock,
      title: "เวลาใกล้หมด",
      description: "ใกล้ปิดรับสมัครแล้ว",
      impact: "พลาดแล้วต้องรออีกนาน"
    },
    {
      icon: FiX,
      title: "ที่นั่งจำกัด",
      description: "รับเพียง 35 คนเท่านั้น",
      impact: "เต็มแล้วไม่มีรอบเพิ่ม"
    },
    {
      icon: FiTrendingDown,
      title: "ราคาขึ้น",
      description: "หลัง 10 คนแรก ราคาจะปรับขึ้น",
      impact: "เสียโอกาสประหยัด 1,000 บาท"
    }
  ];

  return (
    <section className="py-16 px-4 bg-gradient-to-r from-slate-900/80 to-red-900/20 relative overflow-hidden">
      {/* Enhanced Background effects */}
      <div className="absolute inset-0 opacity-30">
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751394399737-blob" 
          alt="Background" 
          className="absolute top-0 left-0 w-1/2 h-full object-cover rounded-r-3xl" 
        />
        <img 
          src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751395126519-blob" 
          alt="Background" 
          className="absolute bottom-0 right-0 w-1/2 h-2/3 object-cover rounded-tl-3xl opacity-80" 
        />
      </div>
      
      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            อย่าปล่อยให้ปัญหาเหล่านี้ <span className="text-red-400">ทำลายอนาคต</span>ของคุณ
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            ทุกวันที่ผ่านไป คือโอกาสที่สูญเสีย และปัญหาที่ยิ่งใหญ่ขึ้น
          </p>
        </motion.div>

        {/* Pain Points Reminder */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="bg-gradient-to-br from-slate-800/70 to-red-900/30 backdrop-blur-sm rounded-xl p-6 border border-red-500/30"
            >
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={point.icon} className="text-white text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-white mb-3">
                  {point.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  {point.description}
                </p>
              </div>
              
              {/* Consequence */}
              <div className="bg-red-500/20 rounded-lg p-3 border border-red-500/30">
                <p className="text-red-400 font-semibold text-sm">
                  ⚠️ ผลที่ตามมา: {point.consequence}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Urgency Factors */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-r from-red-500/20 to-orange-600/20 rounded-2xl p-8 border-2 border-red-500/40 mb-8">
            <h3 className="text-2xl font-bold text-red-400 text-center mb-8">
              ทำไมต้องตัดสินใจ <span className="text-white">วันนี้</span>?
            </h3>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {urgencyFactors.map((factor, index) => (
                <div key={index} className="bg-slate-800/50 rounded-xl p-6 text-center">
                  <SafeIcon icon={factor.icon} className="text-red-400 text-3xl mx-auto mb-3" />
                  <h4 className="text-lg font-bold text-white mb-2">{factor.title}</h4>
                  <p className="text-slate-300 text-sm mb-3">{factor.description}</p>
                  <div className="bg-red-500/20 rounded-lg p-2">
                    <p className="text-red-400 text-xs font-semibold">{factor.impact}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Final Urgency Message */}
            <div className="bg-slate-800/50 rounded-xl p-6 text-center mb-6">
              <h4 className="text-xl font-bold text-amber-400 mb-4">
                คุณจะปล่อยให้โอกาสนี้ผ่านไปหรือไม่?
              </h4>
              <p className="text-white text-lg leading-relaxed mb-4">
                <span className="text-red-400 font-semibold">อีก 1 ปี</span> คุณจะยังคงมีปัญหาเดิม
                <br />
                หรือจะมี<span className="text-green-400 font-semibold">เครื่องมือ</span>ที่ช่วยเปลี่ยนชีวิตได้?
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="bg-red-500/20 rounded-lg p-4 border border-red-500/30">
                  <p className="text-red-400 font-bold mb-2">😢 ถ้าไม่ลงทะเบียน</p>
                  <ul className="text-slate-300 text-left space-y-1">
                    <li>• ปัญหาการเงินยังคงเหมือนเดิม</li>
                    <li>• พลาดโอกาสดีๆ ต่อไป</li>
                    <li>• เสียเวลาลองผิดลองถูก</li>
                    <li>• ไม่รู้วิธีแก้ปัญหาที่ถูกต้อง</li>
                  </ul>
                </div>
                
                <div className="bg-green-500/20 rounded-lg p-4 border border-green-500/30">
                  <p className="text-green-400 font-bold mb-2">🎯 ถ้าลงทะเบียนวันนี้</p>
                  <ul className="text-slate-300 text-left space-y-1">
                    <li>• รู้วิธีเรียกทรัพย์ที่เหมาะกับดวง</li>
                    <li>• มีเครื่องมือใช้งานได้จริง</li>
                    <li>• ประหยัดเวลาลองผิดลองถูก</li>
                    <li>• เปลี่ยนชีวิตการเงินได้ทันที</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Final CTA */}
            <div className="text-center">
              <p className="text-xl text-white mb-6">
                <span className="text-red-400 font-bold">ใกล้ปิดรับสมัครแล้ว</span> อย่าพลาดโอกาส
              </p>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onBooking}
                className="bg-gradient-to-r from-red-500 to-orange-600 text-white px-16 py-6 rounded-full text-2xl font-bold shadow-2xl hover:shadow-red-500/25 transition-all duration-300 animate-pulse flex items-center gap-4 mx-auto"
              >
                <SafeIcon icon={FiPhone} className="text-3xl" />
                ลงทะเบียนก่อนสาย!
              </motion.button>
              
              <p className="text-slate-400 mt-4">
                💡 การตัดสินใจวันนี้ = การเปลี่ยนแปลงชีวิตพรุ่งนี้
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default UrgencySection;